import {
    styled, Button, Box,
    IconButton,
    InputAdornment,
    TextField,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
    Suspense,
    useCallback, useEffect, useState,
    useTransition,
} from 'react';
import debounce from 'lodash/debounce';

import { SetsConfigSearchResults } from 'src/components/SetsConfigPage/SetsConfigSearchResults';
import { SetsConfigSearchResultsSkeleton } from 'src/components/SetsConfigPage/SetsConfigSearchResultsSkeleton';
import { CreateSetsConfigModal } from 'src/components/SetsConfigPage/CreateSetsConfigModal';
import { TitledAppBar } from 'src/components/common/TitledAppBar';
import {
    Add,
} from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useInfiniteQuerySetsConfigs } from 'src/hooks/useInfiniteQuerySetsConfigs';
import { DEFAULT_SETS_CONFIG_LIMIT } from 'src/services/SetsConfigService';

// Minimal component to handle the suspense boundary
const QueryBoundary = ({ searchTerm }: { searchTerm: string }): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);
    const {
        data,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useInfiniteQuerySetsConfigs(accessToken, {
        searchTerm,
        limit: DEFAULT_SETS_CONFIG_LIMIT,
    });

    const results = data?.pages.flatMap((page) => page.results) ?? [];

    return (
        <SetsConfigSearchResults
            fetchNextPage={fetchNextPage}
            hasNextPage={!!hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            results={results}
            searchTerm={searchTerm}
        />
    );
};

const StyledContainer = styled(Box)(({ theme }) => ({
    padding: `0 ${theme.spacing(8)}`,
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(8),
}));

const SearchContainer = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        background: theme.palette.common.white,
    },
}));

export const SetsConfigSearchPage = (): JSX.Element => {
    const [searchInputValue, setSearchInputValue] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [isPending, startTransition] = useTransition();
    const [openCreateSetsConfigModal, setOpenCreateSetsConfigModal] = useState<boolean>(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSetSearchTerm = useCallback(
        debounce((term: string) => {
            startTransition(() => {
                setDebouncedSearchTerm(term);
            });
        }, 300),
        [],
    );

    useEffect(() => {
        debouncedSetSearchTerm(searchInputValue);
        return () => {
            debouncedSetSearchTerm.cancel();
        };
    }, [searchInputValue, debouncedSetSearchTerm]);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setSearchInputValue(e.target.value);
    };

    const renderSearchResults = (): JSX.Element => {
        if (isPending) {
            return <SetsConfigSearchResultsSkeleton />;
        }
        return (
            <Suspense fallback={<SetsConfigSearchResultsSkeleton />}>
                <QueryBoundary searchTerm={debouncedSearchTerm} />
            </Suspense>
        );
    };

    const handleOpenCreateSetsConfigModal = (): void => {
        setOpenCreateSetsConfigModal(true);
    };

    const handleCloseCreateSetsConfigModal = (): void => {
        setOpenCreateSetsConfigModal(false);
    };

    return (
        <>
            <TitledAppBar title="Sets Configuration Tool" />
            <StyledContainer>
                <Box sx={{
                    width: '100%',
                    justifyContent: 'flex-end',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                }}
                >
                    <Button
                        startIcon={<Add />}
                        variant="contained"
                        onClick={handleOpenCreateSetsConfigModal}
                    >
                        Create Sets Config
                    </Button>
                </Box>
                <SearchContainer>
                    <StyledTextField
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton edge="end" type="submit">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        placeholder="Search configs by product key or name..."
                        size="small"
                        sx={{
                            maxWidth: '600px',
                            width: '100%',
                        }}
                        value={searchInputValue}
                        onChange={handleSearchChange}
                    />
                </SearchContainer>
                {renderSearchResults()}
            </StyledContainer>
            <CreateSetsConfigModal
                open={openCreateSetsConfigModal}
                onClose={handleCloseCreateSetsConfigModal}
            />
        </>
    );
};
